import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscriptionAPI } from '../lib/api';
import { isAuthenticated } from '../lib/auth';
import { useTranslation } from '../lib/i18n';
import useDocumentTitle from '../lib/useDocumentTitle';
import { track } from '../lib/analytics';
import AppLayout from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { Check, ArrowRight, Crown, Loader2, Zap, Settings, Users, X as XIcon } from 'lucide-react';

export default function PricingPage() {
  const { t } = useTranslation();
  useDocumentTitle('Preise. Real Content | Starter €29 · Autopilot €99 pro Monat');
  const [loading, setLoading] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated()) {
      fetchSubscription();
    }
  }, []);

  const fetchSubscription = async () => {
    try {
      const response = await subscriptionAPI.getStatus();
      setSubscription(response.data);
    } catch (error) {
      // Silent: subscription badge is cosmetic; if fetching fails we just
      // show the default "Free" tier. 401 is handled globally.
    }
  };

  // `plan` is the backend plan key: 'starter' (€29 casual tier) or
  // 'pro' (legacy alias for Solo €99). `loading` carries the plan key
  // so only the clicked card's button spins.
  const handleUpgrade = async (plan) => {
    if (!isAuthenticated()) {
      navigate('/auth');
      return;
    }

    setLoading(plan);
    // A13. Track checkout intent before redirecting to Stripe.
    // We can't track the actual paid event from here (Stripe webhook
    // does it server-side), but `checkout_started` lets us measure
    // funnel drop-off between pricing-page-view and checkout-initiated.
    track('checkout_started', { plan: plan === 'pro' ? 'solo' : plan });
    try {
      const originUrl = window.location.origin;
      const response = await subscriptionAPI.createCheckout(originUrl, plan);

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('autoPost.saveFailed'));
      setLoading(false);
    }
  };

  // In-app plan switch between the single-seat tiers (Starter ↔
  // Autopilot). No portal, no second checkout: the backend swaps the
  // price on the existing subscription and Stripe prorates.
  const handleChangePlan = async (plan) => {
    const confirmText = plan === 'pro'
      ? (t('pricing.confirmUpgrade')
          || 'Auf Autopilot wechseln? Die Differenz für den laufenden Monat wird anteilig verrechnet.')
      : (t('pricing.confirmDowngrade')
          || 'Zu Starter wechseln? Auto-Posting, Social-Konten und Auto-Import werden deaktiviert. Die Preisdifferenz wird dir gutgeschrieben.');
    if (!window.confirm(confirmText)) return;
    setLoading(plan);
    try {
      await subscriptionAPI.changePlan(plan);
      toast.success(t('pricing.planChanged') || 'Tarif gewechselt.');
      await fetchSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || (t('pricing.planChangeFailed') || 'Tarifwechsel fehlgeschlagen.'));
    } finally {
      setLoading(false);
    }
  };

  // Downgrade to Free = schedule the cancellation at period end. The
  // user keeps everything until the paid time runs out, and can undo
  // it any time before then (handleResume).
  const handleCancel = async () => {
    const dateStr = subscription?.subscription_expires_at
      ? new Date(subscription.subscription_expires_at).toLocaleDateString()
      : null;
    const confirmText = (t('pricing.confirmCancel')
      || 'Zu Free wechseln? Dein Abo läuft bis zum Ende des bezahlten Zeitraums{{date}} normal weiter und endet dann. Du kannst das bis dahin jederzeit rückgängig machen.')
      .replace('{{date}}', dateStr ? ` (${dateStr})` : '');
    if (!window.confirm(confirmText)) return;
    setLoading('cancel');
    try {
      await subscriptionAPI.cancel();
      toast.success(t('pricing.cancelScheduledToast') || 'Kündigung vorgemerkt. Bis zum Periodenende bleibt alles aktiv.');
      await fetchSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || (t('pricing.cancelFailed') || 'Kündigung fehlgeschlagen.'));
    } finally {
      setLoading(false);
    }
  };

  const handleResume = async () => {
    setLoading('resume');
    try {
      await subscriptionAPI.resume();
      toast.success(t('pricing.resumed') || 'Abo läuft weiter. Schön, dass du bleibst!');
      await fetchSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || (t('pricing.resumeFailed') || 'Konnte das Abo nicht fortsetzen.'));
    } finally {
      setLoading(false);
    }
  };

  // Stripe Customer Portal. Still the place for payment methods,
  // invoices, and Team subscriptions. Plan changes and cancellation
  // now happen in-app (above).
  const handleManageSubscription = async () => {
    setOpeningPortal(true);
    try {
      const res = await subscriptionAPI.openPortal();
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error('no portal url');
      }
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          t('pricing.portalFailed') ||
          'Konnte das Abrechnungsportal nicht öffnen.'
      );
      setOpeningPortal(false);
    }
  };

  // Lead with the two outcomes that actually motivate the upgrade –
  // *automatic* posting and *automatic* listing import. Volume / quality
  // / templates are table-stakes for paying users, so they come after.
  const proFeatures = [
    t('pricing.features.autoPost'),
    t('pricing.features.zeroEffort') || 'Null Aufwand: 12h-Vorschau per Mail, du gibst nur frei',
    t('pricing.features.autoImport'),
    t('pricing.features.unlimitedVideos'),
    t('pricing.features.allTemplates'),
    t('pricing.features.branding'),
    t('pricing.features.prioritySupport'),
  ];

  const freeFeatures = [
    t('pricing.features.freeVideos'),
    t('pricing.features.freeQuality'),
    t('pricing.features.freeTemplates'),
    t('pricing.features.freeSupport'),
  ];

  // Tier partitions. `isPro` = Solo-or-above (full feature set, silent
  // caps); `isStarter` = the €29 metered tier; `isPaid` = has any
  // active Stripe subscription (drives the manage-billing button).
  // `subscription === null` means we're still loading. Render the
  // free-tier copy as a neutral fallback rather than flashing a pro
  // page for the half-second before the API resolves.
  const tier = subscription?.subscription_tier;
  const isPro = tier === 'pro' || tier === 'solo' || tier === 'team';
  const isStarter = tier === 'starter';
  const isPaid = isPro || isStarter;
  const isTeam = tier === 'team';
  // A scheduled cancellation: still paid until period end, then Free.
  const cancelScheduled = subscription?.subscription_status === 'cancel_at_period_end';
  const expiresDate = subscription?.subscription_expires_at
    ? new Date(subscription.subscription_expires_at).toLocaleDateString()
    : null;

  const content = (
    <div className="max-w-6xl mx-auto py-6 sm:py-8 px-4 sm:px-0">
      <div className="text-center mb-8 sm:mb-12">
        <h1 className="text-3xl sm:text-4xl font-bold text-[#23211a] mb-3 sm:mb-4">
          {t('pricing.subtitle')}
        </h1>
        <p className="text-[#6f6a5c] text-base sm:text-lg">
          {isPro ? t('pricing.subtitlePro') : t('pricing.subtitleFree')}
        </p>
      </div>

      {/* Current Subscription Status. Stacks on mobile so the manage-
          billing button isn't cramped against the plan label. */}
      {subscription && (
        <div className="mb-6 sm:mb-8 p-4 glass rounded-xl">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              {isPro ? (
                <Crown className="w-6 h-6 text-[#8f6f3a]" />
              ) : (
                <Zap className={isStarter ? 'w-6 h-6 text-[#1f4d3a]' : 'w-6 h-6 text-[#6f6a5c]'} />
              )}
              <div>
                <p className="font-semibold text-[#23211a]">
                  {t('pricing.currentPlan')}: {isPro ? 'Autopilot' : isStarter ? 'Starter' : t('pricing.free')}
                </p>
                {isStarter && subscription.limits && (
                  <p className="text-sm text-[#6f6a5c]">
                    {t('pricing.videosUsedMonthly', {
                      used: subscription.videos_this_month ?? 0,
                      max: subscription.limits.max_videos_monthly,
                    })}
                  </p>
                )}
                {!isPaid && subscription.limits && (
                  <p className="text-sm text-[#6f6a5c]">
                    {t('pricing.videosUsed', {
                      used: subscription.videos_generated_count,
                      max: subscription.limits.max_videos,
                    })}
                  </p>
                )}
              </div>
            </div>
            {isPaid && (
              <div className="flex flex-col items-start sm:items-end gap-1.5">
                {cancelScheduled ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-medium text-[#9a6d1f]">
                      {(t('pricing.cancelScheduled') || 'Abo endet am {{date}}, danach Free')
                        .replace('{{date}}', expiresDate || '—')}
                    </p>
                    <Button
                      size="sm"
                      onClick={handleResume}
                      disabled={loading === 'resume'}
                      className="bg-[#1f4d3a] hover:bg-[#163a2c] text-white text-xs"
                    >
                      {loading === 'resume'
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : (t('pricing.resumeSub') || 'Abo fortsetzen')}
                    </Button>
                  </div>
                ) : subscription.subscription_expires_at && (
                  <p className="text-sm text-[#6f6a5c]">
                    {t('pricing.renews', {
                      date: expiresDate,
                    })}
                  </p>
                )}
                {/* Stripe Customer Portal. Full-page redirect. The
                    portal handles cancel/update-card/invoices, so the
                    button just opens it. Subscription state updates
                    happen via webhook on return. */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleManageSubscription}
                  disabled={openingPortal}
                  className="border-[#d8d0bd] text-[#4a463b] hover:bg-[#efe9db] text-xs"
                >
                  {openingPortal ? (
                    <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> {t('common.loading') || 'Wird geöffnet…'}</>
                  ) : (
                    <><Settings className="w-3.5 h-3.5 mr-1.5" /> {t('pricing.manageSubscription') || 'Abrechnung verwalten'}</>
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Four-tier pricing grid: Free / Starter / Solo / Team.
          On mobile the cards stack; sm 2-up; lg all four side-by-side. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Free Plan */}
        <Card className="glass border-[#e3dccb] flex flex-col">
          <CardHeader>
            <CardTitle className="text-2xl text-[#23211a]">{t('pricing.free')}</CardTitle>
            <CardDescription>{t('pricing.perfectStart')}</CardDescription>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col">
            <div className="mb-6">
              <span className="text-5xl font-bold text-[#23211a]">&euro;0</span>
              <span className="text-[#8a8474]"> / {t('pricing.month') || 'Monat'}</span>
            </div>
            <ul className="space-y-3 mb-8 flex-1">
              {freeFeatures.map((feature, index) => (
                <li key={index} className="flex items-start gap-3 text-[#6f6a5c]">
                  <Check className="w-5 h-5 text-[#8a8474] mt-0.5 shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            {/* Real downgrade: schedules the cancellation at period
                end (undo via the resume button in the status strip).
                Team cancels through the portal because of seats. */}
            <Button
              variant="outline"
              className="w-full border-[#d8d0bd] text-[#4a463b] hover:bg-[#efe9db]"
              onClick={handleCancel}
              disabled={!isPaid || isTeam || cancelScheduled || loading === 'cancel'}
            >
              {loading === 'cancel' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : !isPaid ? (
                t('pricing.currentPlan')
              ) : cancelScheduled ? (
                (t('pricing.freeFrom') || 'Free ab {{date}}').replace('{{date}}', expiresDate || '—')
              ) : (
                t('pricing.downgradeFree') || 'Zu Free wechseln'
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Starter Plan. The MANUAL tier: 5 videos/month created on
            demand, posting stays with the user (no automation — that's
            Autopilot's job, and the card says so explicitly). Launch
            promo: €29 instead of the regular €49. */}
        <Card className="glass border-[#1f4d3a]/40 relative overflow-hidden flex flex-col">
          {isStarter && (
            <div className="absolute top-4 right-4 px-3 py-1 bg-[#1e7a4f] rounded-full text-xs text-white">
              {t('pricing.active')}
            </div>
          )}
          <CardHeader>
            <CardTitle className="text-2xl text-[#23211a] flex items-center gap-2">
              <Zap className="w-6 h-6 text-[#1f4d3a]" />
              Starter
            </CardTitle>
            <CardDescription>{t('pricing.forCasual') || 'Für den gelegentlichen Einsatz'}</CardDescription>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col">
            <div className="mb-6 flex items-baseline gap-2.5 flex-wrap">
              <span className="text-5xl font-bold text-[#23211a]">&euro;29</span>
              <span className="text-[#8a8474]"> / {t('pricing.month') || 'Monat'}</span>
              <s className="text-xl font-medium text-[#9c9585]">&euro;49</s>
            </div>
            <ul className="space-y-3 mb-8 flex-1">
              {[
                t('pricing.features.starterVideos') || '5 Videos / Monat, auf Abruf erstellt',
                t('pricing.features.allTemplates'),
                t('pricing.features.starterImports') || 'Inserate-Import aus Justimmo & Willhaben',
                t('pricing.features.voiceMusic') || 'KI-Voiceover & Musik',
                t('pricing.features.branding'),
                t('pricing.features.hdQuality'),
              ].map((feature, index) => (
                <li key={index} className="flex items-start gap-3 text-[#4a463b]">
                  <Check className="w-5 h-5 text-[#1f4d3a] mt-0.5 shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
              {/* The one thing Starter deliberately does NOT do — named
                  explicitly so nobody buys it expecting automation. */}
              <li className="flex items-start gap-3 text-[#8a8474]">
                <XIcon className="w-5 h-5 text-[#b3543f]/60 mt-0.5 shrink-0" />
                <span>{t('pricing.features.starterNoAutopost') || 'Kein Auto-Posting: das übernimmt der Autopilot-Tarif'}</span>
              </li>
            </ul>
            {/* Free → checkout; Autopilot → in-app downgrade (price
                swap on the existing subscription, difference credited). */}
            <Button
              data-testid="upgrade-to-starter-button"
              className="w-full bg-[#1f4d3a]/90 hover:bg-[#163a2c] text-white"
              onClick={isPro && !isTeam ? () => handleChangePlan('starter') : () => handleUpgrade('starter')}
              disabled={loading === 'starter' || isStarter || isTeam || cancelScheduled}
            >
              {loading === 'starter' ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('pricing.redirecting')}
                </>
              ) : isStarter ? (
                t('pricing.currentPlan')
              ) : isPro ? (
                t('pricing.switchToStarter') || 'Zu Starter wechseln'
              ) : (
                <>
                  {t('pricing.startStarter') || 'Starter wählen'} <ArrowRight className="ml-2 w-4 h-4" />
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Solo Plan. Primary paid tier (replaces old Pro €199) */}
        <Card className="glass border-2 border-[#1f4d3a] relative overflow-hidden flex flex-col">
          {isPro ? (
            <div className="absolute top-4 right-4 px-3 py-1 bg-[#1e7a4f] rounded-full text-xs text-white">
              {t('pricing.active')}
            </div>
          ) : (
            <div className="absolute top-4 right-4 px-3 py-1 bg-[#1f4d3a] rounded-full text-xs text-white">
              {t('pricing.recommended')}
            </div>
          )}
          <CardHeader>
            <CardTitle className="text-2xl text-[#23211a] flex items-center gap-2">
              <Crown className="w-6 h-6 text-[#8f6f3a]" />
              Autopilot
            </CardTitle>
            <CardDescription>{t('pricing.forProfessionals')}</CardDescription>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col">
            <div className="mb-6">
              <span className="text-5xl font-bold text-[#23211a]">&euro;99</span>
              <span className="text-[#8a8474]"> / {t('pricing.month') || 'Monat'}</span>
            </div>
            <ul className="space-y-3 mb-8 flex-1">
              {proFeatures.map((feature, index) => (
                <li key={index} className="flex items-start gap-3 text-[#4a463b]">
                  <Check className="w-5 h-5 text-[#1f4d3a] mt-0.5 shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            {/* Free → checkout; Starter → in-app upgrade (price swap
                on the existing subscription, prorated). */}
            <Button
              data-testid="upgrade-to-pro-button"
              className="w-full bg-[#1f4d3a] hover:bg-[#163a2c] text-white glow-primary"
              onClick={isStarter ? () => handleChangePlan('pro') : () => handleUpgrade('pro')}
              disabled={loading === 'pro' || isPro || cancelScheduled}
            >
              {loading === 'pro' ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('pricing.redirecting')}
                </>
              ) : isPro ? (
                t('pricing.currentPlan')
              ) : isStarter ? (
                <>
                  {t('pricing.upgradeToSolo') || 'Auf Autopilot upgraden'} <ArrowRight className="ml-2 w-4 h-4" />
                </>
              ) : (
                <>
                  {t('pricing.upgradeNow')} <ArrowRight className="ml-2 w-4 h-4" />
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Team Plan. V1.5 live. Per-seat billing via Stripe quantity:
            first 2 seats flat €179, each additional seat €89/mo,
            graduated. The full management UI lives at /team. This card
            is the marketing entry point that routes there. */}
        <Card className="glass border-[#e3dccb] relative overflow-hidden flex flex-col">
          <CardHeader>
            <CardTitle className="text-2xl text-[#23211a] flex items-center gap-2">
              <Users className="w-6 h-6 text-[#8f6f3a]" />
              Team
            </CardTitle>
            <CardDescription>{t('pricing.forAgencies') || 'Für Maklerbüros mit mehreren Mitarbeitern'}</CardDescription>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col">
            <div className="mb-2">
              <span className="text-5xl font-bold text-[#23211a]">&euro;179</span>
              <span className="text-[#8a8474]"> / {t('pricing.month') || 'Monat'}</span>
            </div>
            <p className="text-xs text-[#6f6a5c] mb-6">
              {t('pricing.teamBaseHint') || '2 Nutzer inklusive · jeder zusätzliche Nutzer +€89 / Monat'}
            </p>
            <ul className="space-y-3 mb-8 flex-1">
              {[
                t('pricing.teamFeature1') || 'Alles aus Autopilot, für jeden Mitarbeiter',
                t('pricing.teamFeature2') || 'Multi-Account-Verwaltung',
                t('pricing.teamFeature3') || 'Pro Makler ein eigenes Justimmo-Profil',
                t('pricing.teamFeature4') || 'Zentrale Abrechnung über einen Account',
                t('pricing.teamFeature5') || 'Mitglieder jederzeit hinzufügen/entfernen. Anteilig abgerechnet',
              ].map((feature, index) => (
                <li key={index} className="flex items-start gap-3 text-[#4a463b]">
                  <Check className="w-5 h-5 text-[#8f6f3a] mt-0.5 shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            <Button
              className="w-full bg-[#8f6f3a] hover:bg-[#75592e] text-white"
              onClick={() => navigate('/team')}
              data-testid="pricing-team-cta"
              disabled={tier === 'team'}
            >
              {tier === 'team'
                ? (t('pricing.currentPlan') || 'Aktueller Plan')
                : (tier === 'solo' || tier === 'pro'
                    ? (t('pricing.upgradeToTeam') || 'Auf Team wechseln')
                    : (t('pricing.startTeam') || 'Team starten'))}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* FAQ or Info */}
      <div className="mt-12 text-center">
        <p className="text-[#8a8474] text-sm">
          {t('pricing.questions')}
        </p>
      </div>
    </div>
  );

  // Wrap with AppLayout if authenticated
  if (isAuthenticated()) {
    return <AppLayout>{content}</AppLayout>;
  }

  return (
    <div className="min-h-screen bg-[#f7f3ea]">
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 " />
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#dfe8db] rounded-full blur-3xl" />
      </div>
      <div className="relative px-4 py-20">
        {content}
      </div>
    </div>
  );
}
